"use client";

import { useEffect, useRef, useState } from "react";
import { useInView, useReducedMotion } from "framer-motion";

import { cn } from "@/lib/utils";
import { Testimonial } from "@/services/testimonials/types";

import { CICLO_DA_RECEITA, TestimonialNote } from "./testimonial-note";

/**
 * Segundos que cada recado leva para atravessar a coluna. Multiplicado pela
 * quantidade de recados, mantém a velocidade em px/s parecida independente de
 * quantos depoimentos a API devolver.
 */
const SEGUNDOS_POR_RECADO = 9;

/**
 * O mesmo para o trilho do mobile, e mais lento: lá o recado é largo quase como
 * a tela, então o mesmo tempo por recado daria muito mais px/s. O olho só
 * acompanha um texto que ande bem devagar.
 */
const SEGUNDOS_POR_RECADO_NO_TRILHO = 13;

/**
 * Mínimo de recados por coluna. Com poucos depoimentos a coluna seria mais
 * curta que a parede e o loop mostraria um buraco, então a lista se repete até
 * cobrir.
 */
const RECADOS_POR_COLUNA = 5;

/**
 * Teto de recados por coluna. Existe para a parede não crescer sem limite
 * atrás de uma emenda perfeita: são quatro colunas em duas cópias, então cada
 * recado a mais aqui são oito no documento.
 */
const TETO_POR_COLUNA = 12;

/** Tempo da coreografia de entrada, depois do qual o hover volta a mandar. */
const ENTRADA_MS = 2400;

/**
 * As quatro colunas. O que faz a parede parecer viva e não uma esteira é isto:
 * cada coluna começa num ponto diferente da lista (`recorte`), arranca de uma
 * altura diferente (`topo`), anda num sentido diferente e leva um tempo que não
 * é múltiplo do das outras — assim as colunas nunca voltam a se alinhar.
 *
 * Os recortes não são arbitrários. Com a lista de tamanho n, a coluna que sobe
 * mostra `(r + i) mod n` no lugar i e a que desce mostra `(r - i) mod n`. Duas
 * colunas coincidem quando essas contas se encontram — e uma coincidência põe o
 * mesmo depoimento lado a lado. Com 0 / 3 / 4 / 5 nenhum par se encontra: entre
 * subir e descer a diferença cai sempre num número ímpar, e `2i` nunca é ímpar.
 */
const COLUNAS = [
  { recorte: 0, fator: 1, desce: false, topo: 0, atraso: "0ms" },
  { recorte: 3, fator: 1.26, desce: true, topo: -74, atraso: "130ms" },
  { recorte: 4, fator: 1.09, desce: false, topo: -28, atraso: "260ms" },
  { recorte: 5, fator: 1.41, desce: true, topo: -96, atraso: "390ms" },
] as const;

interface TestimonialsWallProps {
  testimonials: Testimonial[];
}

/**
 * Quantos recados cada coluna carrega.
 *
 * Duas emendas se encontram no fim da coluna, porque ali o último bilhete
 * encosta no primeiro da repetição seguinte:
 *
 * - a da **lista**: se o total não for múltiplo de `testimonials.length`, o
 *   primeiro depoimento reaparece colado no último — dois bilhetes com o mesmo
 *   texto, um em cima do outro;
 * - a da **receita** (`CICLO_DA_RECEITA`): se também não for múltiplo dela, o
 *   papel e a voz do lugar 0 se repetem ali.
 *
 * A primeira é obrigatória. A segunda só é atendida quando sai barato: casar as
 * duas com 8 depoimentos pediria 24 recados por coluna, quase 200 no documento
 * por causa de uma emenda que passa alguns segundos a cada volta.
 */
function recadosPorColuna(total: number): number {
  const multiploDaLista = Math.ceil(RECADOS_POR_COLUNA / total) * total;

  for (let n = multiploDaLista; n <= TETO_POR_COLUNA; n += total) {
    if (n % CICLO_DA_RECEITA === 0) {
      return n;
    }
  }

  return multiploDaLista;
}

/**
 * Parede de recados: colunas verticais em deriva contínua no desktop, trilho
 * horizontal no mobile.
 *
 * A deriva é sempre CSS, com as keyframes que o resto do produto já usa:
 * `animate-marquee-vertical` nas colunas, e a `marquee` deitada no trilho, essa
 * escrita em `home.style.css` porque a duração ali é outra. É o mesmo desenho
 * nos dois: duas cópias da lista, cada uma andando a própria medida mais o vão,
 * e a emenda cai onde o conteúdo se repete.
 *
 * Já foi JS mexendo em `scrollLeft` quadro a quadro, e ficou aos trancos: no
 * ritmo do trilho o passo de um quadro é fração de pixel, o scroll arredonda
 * para zero ou um, e o que anda em degraus de um pixel parece travando. Em
 * `transform` quem move é o compositor e os recados são pintados uma vez.
 *
 * No mobile a parede vira o trilho: a primeira coluna carrega os depoimentos
 * todos, então nada se perde ao esconder as outras. Empilhados, eles custariam
 * várias telas de rolagem para atravessar uma seção que é opcional; deitados,
 * a seção cabe numa tela e passa sozinha.
 *
 * O JS aqui decide duas coisas, e a parede funciona sem ele: quando a
 * coreografia de entrada roda, e quando o trilho para. Parar importa porque um
 * texto que anda e não pode ser parado não se lê, e em toque não existe hover —
 * então o papel que o hover cumpre no desktop fica com o dedo: encostou, para;
 * soltou, volta a andar.
 */
export function TestimonialsWall({ testimonials }: TestimonialsWallProps) {
  const paredeRef = useRef<HTMLDivElement>(null);
  const emVista = useInView(paredeRef, { once: true, amount: 0.15 });
  const reduzirMovimento = useReducedMotion();

  // Antes de montar, ninguém está escondido: sem JS a parede aparece inteira.
  const [montado, setMontado] = useState(false);
  const [assentou, setAssentou] = useState(false);
  const [segurando, setSegurando] = useState(false);

  useEffect(() => setMontado(true), []);

  const coreografar = montado && !reduzirMovimento;
  const entrando = coreografar && emVista && !assentou;

  useEffect(() => {
    if (!entrando) {
      return;
    }

    const t = setTimeout(() => setAssentou(true), ENTRADA_MS);
    return () => clearTimeout(t);
  }, [entrando]);

  const porColuna = recadosPorColuna(testimonials.length);
  // Com um ou dois depoimentos, quatro colunas seriam a mesma pessoa lado a
  // lado quatro vezes — a parede encolhe junto com a lista.
  const colunas = COLUNAS.slice(
    0,
    Math.min(COLUNAS.length, testimonials.length),
  );
  // O trilho mostra a lista uma vez por cópia, e não os `porColuna` recados que
  // a coluna precisa para fechar o próprio loop.
  const duracaoDoTrilho = testimonials.length * SEGUNDOS_POR_RECADO_NO_TRILHO;

  return (
    <div
      ref={paredeRef}
      // O toque no lugar do hover. Fica na parede, e não em cada cópia, porque
      // o que para é o trilho inteiro — como no desktop, onde o cursor sobre um
      // recado segura a parede toda.
      data-segurando={segurando || undefined}
      onPointerDown={() => setSegurando(true)}
      onPointerUp={() => setSegurando(false)}
      onPointerCancel={() => setSegurando(false)}
      onPointerLeave={() => setSegurando(false)}
      className={cn(
        // A largura é contida de propósito. Solta até a borda, a coluna fica
        // larga, o recado fica baixo e cabem quatro por coluna — dezesseis
        // lugares para seis depoimentos, e a mesma frase aparece quatro vezes
        // na mesma tela. Coluna estreita = recado alto = menos repetição.
        "recado-parede mx-auto w-full max-w-[1240px]",
        "[--gap:18px] [gap:var(--gap)]",
        // Mobile: o trilho corre de ponta a ponta e as bordas o esmaecem, então
        // não há padding lateral para segurar recado nenhum. O `py` guarda a
        // sombra dura e o canto do bilhete torto, que o `overflow` cortaria.
        "max-md:py-3",
        "md:grid md:h-[600px] md:grid-cols-3 md:overflow-hidden md:px-7",
        "xl:grid-cols-4 xl:px-10",
      )}
    >
      {colunas.map((coluna, colunaIndex) => {
        // As colunas que descem leem a lista de trás para frente: com o mesmo
        // ciclo e só um deslocamento, as quatro mostrariam os depoimentos na
        // mesma ordem, e a parede viraria quatro cópias da mesma esteira.
        const recados = Array.from({ length: porColuna }, (_, i) => {
          const passo = coluna.desce ? -i : i;
          const total = testimonials.length;
          return testimonials[
            (((passo + coluna.recorte) % total) + total) % total
          ];
        });
        const duracaoS = Math.round(
          recados.length * SEGUNDOS_POR_RECADO * coluna.fator,
        );

        return (
          <div
            key={colunaIndex}
            className={cn(
              // A quarta coluna só existe onde há largura para ela respirar.
              colunaIndex === 3 && "hidden xl:block",
              // No mobile só a primeira coluna aparece — e ela tem a lista toda.
              colunaIndex > 0 && "max-md:hidden",
            )}
          >
            <div
              className="recado-pilha flex [gap:var(--gap)] md:flex-col"
              style={{ ["--topo" as string]: `${coluna.topo}px` }}
            >
              {[0, 1].map((copia) => (
                <div
                  key={copia}
                  // A segunda cópia é a mesma lista de novo: é o que faz a
                  // emenda do loop cair onde o conteúdo se repete — na vertical
                  // das colunas e na horizontal do trilho.
                  aria-hidden={copia === 1 || undefined}
                  className={cn(
                    "recado-coluna flex shrink-0 [gap:var(--gap)]",
                    "md:flex-col",
                    // De pé a partir de `md`. Deitada, a deriva do trilho mora
                    // em `home.style.css`: a duração ali é outra, e as duas
                    // regras se separam por media query em vez de disputar
                    // ordem com o utilitário.
                    "md:animate-marquee-vertical md:motion-reduce:animate-none",
                    coluna.desce && "md:[animation-direction:reverse]",
                    copia === 1 && "recado-coluna-copia",
                  )}
                  style={{
                    ["--duration" as string]: `${duracaoS}s`,
                    ["--duration-trilho" as string]: `${duracaoDoTrilho}s`,
                  }}
                >
                  {recados.map((testimonial, i) => (
                    <div
                      key={`${colunaIndex}-${copia}-${i}-${testimonial.id}`}
                      className={cn(
                        // Escondido só enquanto a seção não chegou: a queda dos
                        // bilhetes precisa de um antes.
                        coreografar && !emVista && "opacity-0",
                        // No trilho cada recado é largo quase como a tela: o
                        // vizinho espia na borda e diz que há mais de lado.
                        "max-md:w-[78vw] max-md:max-w-[320px] max-md:shrink-0",
                        // Cada cópia carrega a lista inteira mais as repetições
                        // que fecham o loop da coluna. No trilho elas sobram: a
                        // volta ali é a cópia seguinte, e mantê-las poria a
                        // mesma pessoa duas vezes dentro de um ciclo só.
                        colunaIndex === 0 &&
                          i >= testimonials.length &&
                          "max-md:hidden",
                      )}
                      // A parede repete as mesmas pessoas em colunas
                      // diferentes: só a primeira passagem é lida em voz alta.
                      aria-hidden={colunaIndex > 0 || undefined}
                    >
                      <TestimonialNote
                        testimonial={testimonial}
                        coluna={colunaIndex}
                        lugar={i}
                        atraso={coluna.atraso}
                        animarEntrada={entrando}
                      />
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
